import { useEffect, useMemo, useState } from "react";

/** Shared rows-per-page options across every paginated table. */
export const TABLE_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50] as const;

/** Default rows-per-page for client-side paginated tables. */
export const DEFAULT_TABLE_PAGE_SIZE = 20;

export interface TablePaginationState<T> {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  totalPages: number;
  totalCount: number;
  pageRows: T[];
}

/**
 * Client-side pagination for tables that already have their full dataset in
 * memory. Owns both `page` and `pageSize` so it can drive the shared
 * `TablePagination` footer (rows-per-page select + prev/next).
 *
 * Sorting/filtering must be applied to `rows` before it is passed in, so the
 * page always reflects the full ordered dataset.
 */
export function useTablePagination<T>(
  rows: T[] | undefined,
  initialPageSize: number = DEFAULT_TABLE_PAGE_SIZE,
): TablePaginationState<T> {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const totalCount = rows?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Reset out-of-range pages (filters shrank the dataset, page size grew, …).
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const safePage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return (rows ?? []).slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPage(1);
  };

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageRows,
  };
}